import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "@/api/api";
import { es } from "date-fns/locale";
import { format, parseISO } from "date-fns";
import { API_ROUTES } from "@/api/routes";
import { FiLogOut } from "react-icons/fi";
import { FaUserFriends } from "react-icons/fa";
import EmpresaBalanceHeader from "./EmpresaBalanceHeader";
import { useSession } from "@/context/SessionProvider";
import { useBuildWatcher } from "@/hooks/useBuildWatcher";

const UI_MODE_KEY = "ui_mode";
const isNocAdmin = (u) => [1, 5].includes(Number(u?.perfil_id));

const USER_BTN = "text-white bg-[#0f4e5a] hover:bg-[#0c3f49]";
const ADMIN_BTN = "text-white bg-red-600 hover:bg-red-700";
const BASE_BTN =
    "text-sm font-bold px-3 py-1 rounded flex items-center gap-2 shadow-sm transition-colors";

const readUserName = (u) =>
    u?.nombre || u?.Nombre || u?.nombreCompleto || "Usuario";

async function fetchFechaCorte() {
    try {
        const response = await axios.get(API_ROUTES.fechaCorte);
        return response.data;
    } catch (error) {
        console.error('Error al obtener fecha de corte:', error);
        const ayer = new Date();
        ayer.setDate(ayer.getDate() - 1);
        return {
            fecha_corte: format(ayer, 'yyyy-MM-dd'),
            tiene_datos: false
        };
    }
}

export default function Header({ empresa }) {
    const navigate = useNavigate();
    const { user, loading: sessionLoading } = useSession();

    const [fechaCorte, setFechaCorte] = useState(null);
    const [fechaCorteLoading, setFechaCorteLoading] = useState(true);

    useEffect(() => {
        let mounted = true;

        async function loadFechaCorte() {
            setFechaCorteLoading(true);
            const data = await fetchFechaCorte();
            if (mounted) {

                setFechaCorte(data);
                setFechaCorteLoading(false);
            }
        }

        loadFechaCorte();

        return () => {
            mounted = false;
        };
    }, []);

    const fechaCorteFormateada = useMemo(() => {
        if (!fechaCorte?.fecha_corte) return '...';

        try {
            const fecha = parseISO(fechaCorte.fecha_corte);
            const formateada = format(fecha, "dd-MM-yyyy", { locale: es });
            return formateada;
        } catch (error) {
            console.error('Error al formatear fecha:', error);
            return fechaCorte.fecha_corte;
        }
    }, [fechaCorte]);

    const { hasUpdate, applyUpdate } = useBuildWatcher(60000);

    const computeMode = (u) => {
        const cached = localStorage.getItem(UI_MODE_KEY);
        if (isNocAdmin(u)) return cached === "admin" || cached === "user" ? cached : "admin";
        return "user";
    };

    const [mode, setMode] = useState(() => computeMode(null));

    useEffect(() => {
        if (sessionLoading) return;
        const next = computeMode(user);
        setMode(next);
        localStorage.setItem(UI_MODE_KEY, next);
        window.dispatchEvent(new CustomEvent("ui-mode-changed", { detail: next }));
    }, [user, sessionLoading]);

    const admin = isNocAdmin(user);
    const nombreUsuario = useMemo(() => readUserName(user), [user]);

    const handleToggleMode = () => {
        if (!admin || sessionLoading) return;
        const next = mode === "admin" ? "user" : "admin";
        setMode(next);
        localStorage.setItem(UI_MODE_KEY, next);
        window.dispatchEvent(new CustomEvent("ui-mode-changed", { detail: next }));
    };

    const btnClass = admin
        ? mode === "admin"
            ? ADMIN_BTN
            : USER_BTN
        : `${USER_BTN} cursor-not-allowed pointer-events-none`;

    async function handleLogout() {
        try {
            await axios.post(API_ROUTES.logout, null, { withCredentials: true });
        } catch { }
        navigate("/");
    }

    return (
        <header className="flex justify-between items-center p-4 bg-gray-100">
            <div className="flex items-center gap-3 flex-wrap">
                <button
                    type="button"
                    onClick={handleToggleMode}
                    title={
                        admin
                            ? `Cambiar a vista ${mode === "admin" ? "Usuario" : "Administrador"}`
                            : "Vista Cliente (bloqueado)"
                    }
                    className={`${BASE_BTN} ${btnClass}`}
                    disabled={!admin || sessionLoading}
                >
                    <FaUserFriends />
                    <span>Hola {sessionLoading ? "…" : nombreUsuario}</span>
                    <span className="ml-2 text-[11px] uppercase bg-white/20 px-2 py-0.5 rounded">
                        {mode}
                    </span>
                </button>

                <EmpresaBalanceHeader empresa={empresa} />

                <span className="text-xs text-slate-500 italic">
                    {fechaCorteLoading ? 'Cargando fecha...' : `Saldo al ${fechaCorteFormateada}`}
                </span>

                {hasUpdate && (
                    <button
                        onClick={applyUpdate}
                        className="ml-2 text-xs font-semibold px-3 py-1 rounded-full bg-amber-200 text-amber-900 border border-amber-300 hover:bg-amber-300 shadow-sm"
                        title="Hay una nueva versión. Haz clic para actualizar"
                    >
                        Nueva versión disponible — Actualizar
                    </button>
                )}
            </div>

            <button
                onClick={handleLogout}
                className="text-xl text-gray-700 hover:text-red-600"
                title="Cerrar sesión"
                type="button"
            >
                <FiLogOut />
            </button>
        </header>
    );
}